/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import classnames from 'classnames';
import { styled } from '@mui/material/styles';
import { Table as TableMaterial } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Collapse, { collapseClasses } from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Button, { buttonClasses } from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer, { tableContainerClasses } from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TablePagination, { tablePaginationClasses } from '@mui/material/TablePagination';
import CircleSpinnerLoading from '@components/CircleSpinnerLoading';
import { capitalizeFirstLetter } from '@utils';
import { MILLION_MULTIPLIER } from '@constants/system';

import styles from './table.module.scss';

const StyledCollapse = styled(Collapse)(({ theme }) => ({
  [`& .${collapseClasses.wrapperInner}`]: {
    width: 'unset',
  },
}));
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  [`&.${tableContainerClasses.root}`]: {
    borderRadius: '10px',
    boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.03)',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#424242',
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '21px',
    color: '#FFFFFF',
  },
  [`&.${tableCellClasses.body}`]: {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '18px',
    color: '#000000',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  [`&.${tablePaginationClasses.root}`]: {
    overflow: 'initial',
  },
  [`& .${tablePaginationClasses.selectLabel}`]: {
    fontSize: 14,
  },
  [`& .${tablePaginationClasses.select}`]: {
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${tablePaginationClasses.displayedRows}`]: {
    fontSize: 14,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  [`&.${buttonClasses.root}`]: {
    color: '#4F4F4F',
    backgroundColor: '#FFFFFF',
    /* To highlight clicking button on Mobile */
    '&:active': {
      color: '#FFFFFF',
      backgroundColor: '#2D9CDB',
    },
    /* Unnecessary(check it) */
    // '@media (hover: none), (pointer: coarse)': {
    //   [`&.${buttonClasses.root}:hover`]: {
    //     color: '#4F4F4F',
    //     backgroundColor: '#FFFFFF',
    //   },
    // },
    '@media (hover: hover)': {
      [`&.${buttonClasses.root}:hover`]: {
        color: '#FFFFFF',
        backgroundColor: '#2D9CDB',
      },
    },
  },
  [`&.${buttonClasses.disabled}`]: {
    color: 'rgba(0, 0, 0, 0.26)',
    backgroundColor: 'rgba(0, 0, 0, 0.12);',
    boxShadow: 'none',
  },
}));

const TxDetails = ({ txDescription = [] }) => {
  return (
    <div className={styles.TxDetailsContainer}>
      {txDescription?.map((description, index) => {
        return (
          <div className={styles.TxDetails} key={`${description.key}_${index}`}>
            <div className={styles.TxDetailsKey}>
              <p className={styles.TxDetailsKeyText}>
                {capitalizeFirstLetter(description.key).replace(/_/gi, ' ')}:
              </p>
            </div>
            <div className={styles.TxDetailsValue}>
              <p className={styles.TxDetailsValueText}>
                {description.value.slice(-6) === 'uhoney'
                  ? `${parseFloat(description.value) / MILLION_MULTIPLIER} honey`
                  : description.value}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CollapseRow = ({
  row = [],
  customCellWidth = false,
  customCellWhiteSpace = false,
  customCellAlign = false,
  customTextOverflow = false,
  children,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <StyledTableRow key={Math.random().toString()}>
        <StyledTableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </StyledTableCell>
        {row.map((rowField, indexRow) => {
          return (
            <StyledTableCell
              align={customCellAlign || (indexRow > 0 ? 'right' : 'left')}
              key={`collapseRowField_${indexRow}`}
              sx={{
                width: customCellWidth || 'inherit',
                whiteSpace: customCellWhiteSpace || 'inherit',
                textOverflow: customTextOverflow || 'inherit',
              }}
            >
              {rowField}
            </StyledTableCell>
          );
        })}
      </StyledTableRow>
      <StyledTableRow key={Math.random().toString()}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <StyledCollapse in={open} timeout="auto" unmountOnExit key={Math.random().toString()}>
            {children}
          </StyledCollapse>
        </TableCell>
      </StyledTableRow>
    </>
  );
};

const Table = ({
  header = ['nameTable', 'idTable'],
  rows = [
    ['name1', 'id1'],
    ['name2', 'id2'],
  ],
  title = 'TitleTable',
  customMt = false,
  customWidth = false,
  customCellWidth = false,
  customCellWhiteSpace = false,
  customCellAlign = false,
  customTextOverflow = false,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalCount,
  isActiveTable = false,
  isSpinning = false,
  showFirstButton = true, // If true, show the first-page button.
  showLastButton = true, // If true, show the last-page button.
  isHugeTotalCount = false,
  isEnabledPaginationButtons = false, // enabled/disabled button huge next or prevue
  isNeedAuxPagination = false, // if isHugeTotalCount & need 
  onClickPrevButton = () => {}, // if isHugeTotalCount
  disabledPrevButton = false,
  disabledNextButton = false,
  onClickNextButton = () => {}, // if isHugeTotalCount
  classNames = {},
  isCollapsibleTable = false,
}) => {
  const [pageAuxPagination, setPageAuxPagination] = React.useState(0);
  const [rowsPerPageAuxPagination, setRowsPerPageAuxPagination] = React.useState(5);
  /* Pagination logic */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  /* AuxPagination work with flag isNeedPagination */
  const handleChangePageAuxPagination = (event, pageAuxPagination) => {
    setPageAuxPagination(pageAuxPagination);
  };
  const handleChangeRowsPerPageAuxPagination = (event) => {
    setRowsPerPageAuxPagination(parseInt(event.target.value, 10));
    setPageAuxPagination(0);
  };

  if (totalCount === 0) {
    return (
      <StyledTableContainer
        sx={{ width: 'auto', position: 'relative', mt: 2 }}
        component={Paper}
        className={classnames(!isActiveTable && styles.TableContainer__DisplayNone)}
      >
        <TableMaterial sx={{ width: 400 }} aria-label="customized table">
          <TableBody>
            <StyledTableRow>
              <StyledTableCell component="th" scope="row">
                Empty Table
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </TableMaterial>
      </StyledTableContainer>
    );
  }
  return (
    <>
      {!!title && (
        <p
          className={classnames(
            styles.TitleTable,
            !isActiveTable && styles.TableContainer__DisplayNone,
            classNames.TitleTable,
          )}
        >
          {title}
        </p>
      )}
      <div className={styles.MainTableContainer}>
        <StyledTableContainer
          sx={{ width: customWidth || 'auto', mt: customMt || 2 }}
          component={Paper}
          className={classnames(!isActiveTable && styles.TableContainer__DisplayNone)}
        >
          <TableMaterial aria-label="customized table">
            <TableHead>
              <TableRow>
                {header.map((headerName, headerIndex) => {
                  return (
                    <StyledTableCell
                      align={
                        isCollapsibleTable
                          ? headerIndex > 1
                            ? 'right'
                            : 'left'
                          : headerIndex > 0
                          ? 'right'
                          : 'left'
                      }
                      key={`headerName_${headerIndex}`}
                    >
                      {headerName}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {isSpinning ? (
                <tr>
                  <td>
                    <CircleSpinnerLoading
                      classNames={{
                        Container: styles.CircleSpinnerContainer,
                        Spinner: styles.CircleSpinner,
                      }}
                    />
                  </td>
                </tr>
              ) : isNeedAuxPagination ? (
                rows
                  .slice(
                    pageAuxPagination * rowsPerPageAuxPagination,
                    pageAuxPagination * rowsPerPageAuxPagination + rowsPerPageAuxPagination,
                  )
                  .map((row, index) =>
                    !isCollapsibleTable ? (
                      <StyledTableRow key={`row_${index}`}>
                        {row.map((rowField, rowIndex) => {
                          return (
                            <StyledTableCell
                              align={customCellAlign || (rowIndex > 0 ? 'right' : 'left')}
                              key={`rowField_${rowIndex}`}
                              sx={{
                                width: customCellWidth || 'inherit',
                                whiteSpace: customCellWhiteSpace || 'inherit',
                                textOverflow: customTextOverflow || 'inherit',
                              }}
                            >
                              {rowField}
                            </StyledTableCell>
                          );
                        })}
                      </StyledTableRow>
                    ) : (
                      <CollapseRow
                        key={`collapseRow_${index}`}
                        row={row?.txInfo}
                        index={index}
                        customCellWidth={customCellWidth}
                        customCellWhiteSpace={customCellWhiteSpace}
                        customCellAlign={customCellAlign}
                        customTextOverflow={customTextOverflow}
                      >
                        <TxDetails
                          txDescription={row?.txDescription}
                          key={Math.random().toString()}
                        />
                      </CollapseRow>
                    ),
                  )
              ) : (
                rows.map((row, index) =>
                  !isCollapsibleTable ? (
                    <StyledTableRow key={`row_${index}`}>
                      {row.map((rowField, rowIndex) => {
                        return (
                          <StyledTableCell
                            align={customCellAlign || (rowIndex > 0 ? 'right' : 'left')}
                            key={`rowField_${rowIndex}`}
                            sx={{
                              width: customCellWidth || 'inherit',
                              whiteSpace: customCellWhiteSpace || 'inherit',
                              textOverflow: customTextOverflow || 'inherit',
                            }}
                          >
                            {rowField}
                          </StyledTableCell>
                        );
                      })}
                    </StyledTableRow>
                  ) : (
                    <CollapseRow
                      key={`collapseRow_${index}`}
                      row={row?.txInfo}
                      index={index}
                      customCellWidth={customCellWidth}
                      customCellWhiteSpace={customCellWhiteSpace}
                      customCellAlign={customCellAlign}
                      customTextOverflow={customTextOverflow}
                    >
                      <TxDetails
                        txDescription={row?.txDescription}
                        key={Math.random().toString()}
                      />
                    </CollapseRow>
                  ),
                )
              )}
            </TableBody>
          </TableMaterial>
          {!isHugeTotalCount && (
            <StyledTablePagination
              /* Customizes the options of the rows per page select field. 
                If less than two options are available, 
                no select field will be displayed. 
                Use -1 for the value with a custom label to show all the rows. */
              rowsPerPageOptions={[2, 5, 10, 25]}
              component="div" // The component used for the root node. Either a string to use a HTML element or a component.
              count={totalCount} // The total number of rows. To enable server side pagination for an unknown number of items, provide -1.
              rowsPerPage={rowsPerPage} // The number of rows per page. Set -1 to display all the rows.
              page={page} // The zero-based index of the current page.
              onPageChange={handleChangePage} // Callback fired when the page is changed.
              onRowsPerPageChange={handleChangeRowsPerPage} // Callback fired when the number of rows per page is changed.
              showFirstButton={showFirstButton} // If true, show the first-page button.
              showLastButton={showLastButton} // If true, show the last-page button.
            />
          )}
          {isNeedAuxPagination && (
            <StyledTablePagination
              rowsPerPageOptions={[2, 5, 10, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPageAuxPagination}
              page={pageAuxPagination}
              onPageChange={handleChangePageAuxPagination}
              onRowsPerPageChange={handleChangeRowsPerPageAuxPagination}
              showFirstButton={showFirstButton}
              showLastButton={showLastButton}
            />
          )}
        </StyledTableContainer>
        {(isHugeTotalCount || isEnabledPaginationButtons) && (
          <div className={styles.PaginationButtonContainer}>
            <StyledButton
              variant="contained"
              onClick={onClickPrevButton}
              startIcon={<ArrowBackIosIcon />}
              sx={{ margin: '16px' }}
              disabled={disabledPrevButton}
              className={styles.PaginationButton}
            >
              Previous
            </StyledButton>
            <StyledButton
              variant="contained"
              onClick={onClickNextButton}
              endIcon={<ArrowForwardIosIcon />}
              sx={{ margin: '16px 0' }}
              disabled={disabledNextButton}
              className={styles.PaginationButton}
            >
              Next
            </StyledButton>
          </div>
        )}
      </div>
    </>
  );
};

export default Table;
